var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useCallback } from "react";
import { Howl } from "howler";
import ImgStopWhite from "../assets/images/pause-w.svg";
import ImgStopBlue from "../assets/images/stop.svg";
import ImgPlayWhite from "../assets/images/play.svg";
// Used to initialize functions to no-ops.
// This is prevents this function from changing reference equality between
// React component re-renders, which will cause unecessary updates that
// can lead to bugs in side-effects (via useEffect).
const NOOP = () => { };
// Function to stop most recently played audio.
// This is used to ensure that only one audio clip is playing at a time.
// In particular, newly started audio, calls this function to stop other audio.
let STOP_ACTIVE_AUDIO = NOOP;
// Converts a duration in seconds into a minutes/seconds duration string.
const secondsToTimeStr = (duration) => {
    const mins = Math.floor(duration / 60);
    const secs = Math.round(duration) % 60;
    if (secs < 10) {
        return `${mins}:0${secs}`;
    }
    return `${mins}:${secs}`;
};
// Calls the given callback with the current audio position as long as the
// given audio is playing.
const updatePositionLoop = (sound, callback) => {
    if (sound.state() === "unloaded") {
        return;
    }
    const pos = sound.seek();
    if (!(pos instanceof Howl) && pos !== 0) {
        callback(pos);
    }
    if (sound.playing()) {
        setTimeout(() => updatePositionLoop(sound, callback), 100);
    }
};
const AudioWrapper = ({ playback, seek = 0, src, format, preload = false, onUnloaded = NOOP, onLoading = NOOP, onLoaded = NOOP, onEnd = NOOP, onAudioChange = NOOP, onPlaying, onLoadError = NOOP, onPlayError = NOOP }) => {
    const [sound, setSound] = useState();
    const [state, setState] = useState("unloaded");
    const createHowl = useCallback(() => {
        setState(preload ? "loading" : "unloaded");
        onAudioChange();
        return new Howl({
            src,
            format,
            preload,
            onload: () => setState("loaded"),
            onend: onEnd,
            onloaderror: (_id, error) => {
                onLoadError(error);
            },
            onplayerror: (_id, error) => {
                onPlayError(error);
            }
        });
    }, [src, format, preload, onEnd, onAudioChange, onLoadError, onPlayError]);
    useEffect(() => {
        const newSound = createHowl();
        setSound(newSound);
        return () => {
            newSound.unload();
            setState("unloaded");
        };
    }, [createHowl]);
    useEffect(() => {
        switch (state) {
            case "unloaded":
                onUnloaded();
                break;
            case "loading":
                onLoading();
                break;
            case "loaded":
                onLoaded(sound ? sound.duration() : -1);
                break;
            default:
                break;
        }
    }, [sound, state, onUnloaded, onLoading, onLoaded]);
    useEffect(() => {
        if (!sound) {
            return;
        }
        switch (playback) {
            case "play": {
                if (state === "unloaded") {
                    sound.load();
                }
                else if (state === "loaded" && !sound.playing()) {
                    sound.play();
                    if (onPlaying) {
                        updatePositionLoop(sound, onPlaying);
                    }
                }
                break;
            }
            case "pause":
                sound.pause();
                break;
            case "stop":
                sound.stop();
                break;
            default:
                break;
        }
    }, [playback, state, sound, onPlaying]);
    useEffect(() => {
        if (sound) {
            sound.seek(seek);
        }
    }, [sound, seek]);
    return React.createElement(React.Fragment, null);
};
const AudioPlayerButton = ({ src, format, preload = false, autoplay = false, theme = "", onPlay = NOOP, onStop = NOOP, onError = NOOP }) => {
    const [playing, setPlaying] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // Track which hooks have been run.
    // Ensures that onPlay and onStop are only called once per play/stop even if
    // the callbacks change.
    const [ranHookPlay, setRanHookPlay] = useState(false);
    const [ranHookStop, setRanHookStop] = useState(false);
    const onUnloaded = useCallback(() => {
        setLoaded(false);
    }, []);
    const onLoading = useCallback(() => {
        setLoaded(false);
    }, []);
    const onLoaded = useCallback(() => {
        setLoaded(true);
    }, []);
    const onEnd = useCallback(() => {
        setPlaying(false);
    }, []);
    const onAudioChange = useCallback(() => {
        if (autoplay) {
            setPlaying(true);
        }
    }, [autoplay]);
    const onLoadError = useCallback(err => {
        setLoaded(false);
        setPlaying(false);
        onError(err);
    }, [onError]);
    const onPlayError = useCallback(err => {
        setPlaying(false);
        onError(err);
    }, [onError]);
    const onStopActiveAudio = useCallback(() => {
        setPlaying(false);
    }, []);
    useEffect(() => {
        if (playing && !ranHookPlay) {
            setRanHookStop(false);
            setRanHookPlay(true);
            onPlay();
        }
        else if (!playing && !ranHookStop) {
            setRanHookStop(true);
            setRanHookPlay(false);
            onStop();
        }
    }, [playing, ranHookPlay, ranHookStop, onPlay, onStop]);
    useEffect(() => {
        if (playing) {
            if (STOP_ACTIVE_AUDIO) {
                STOP_ACTIVE_AUDIO();
            }
            STOP_ACTIVE_AUDIO = onStopActiveAudio;
        }
        else if (STOP_ACTIVE_AUDIO === onStopActiveAudio) {
            STOP_ACTIVE_AUDIO = () => { };
        }
    }, [playing, onStopActiveAudio]);
    return (React.createElement("div", { className: `audioplayer-button ${playing ? "active" : ""} ${theme}` },
        React.createElement(AudioWrapper, Object.assign({}, {
            playback: playing ? "play" : "stop",
            src,
            format,
            preload,
            onUnloaded,
            onLoading,
            onLoaded,
            onEnd,
            onAudioChange,
            onLoadError,
            onPlayError
        })),
        React.createElement("button", { type: "button", onClick: () => setPlaying(!playing) },
            React.createElement("img", { className: "play-img", src: ImgPlayWhite, alt: "play" }),
            React.createElement("img", { className: "stop-img", src: ImgStopBlue, alt: "stop" }),
            playing && !loaded && React.createElement("div", { className: "loading" }))));
};
const AudioPlayerSeekbar = ({ src, format, preload = false, autoplay = false, theme = "", onPlay = NOOP, onStop = NOOP, onError = NOOP }) => {
    const [playing, setPlaying] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [seeking, setSeeking] = useState(false);
    const [seek, setSeek] = useState(0);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);
    const onUnloaded = useCallback(() => {
        setLoaded(false);
        setSeek(0);
    }, []);
    const onLoading = useCallback(() => {
        setLoaded(false);
    }, []);
    const onLoaded = useCallback(dur => {
        setLoaded(true);
        setDuration(dur);
    }, []);
    const onEnd = useCallback(() => {
        setPlaying(false);
        setPosition(duration);
    }, [duration]);
    const onAudioChange = useCallback(() => {
        if (autoplay) {
            setPlaying(true);
        }
    }, [autoplay]);
    const onPlaying = useCallback(pos => {
        setPosition(pos);
    }, []);
    const onLoadError = useCallback(err => {
        setLoaded(false);
        setPlaying(false);
        onError(err);
    }, [onError]);
    const onPlayError = useCallback(err => {
        setPlaying(false);
        onError(err);
    }, [onError]);
    const onStopActiveAudio = useCallback(() => {
        setPlaying(false);
    }, []);
    useEffect(() => {
        if (playing) {
            onPlay();
        }
        else {
            onStop();
        }
    }, [playing, onPlay, onStop]);
    useEffect(() => {
        if (playing) {
            if (STOP_ACTIVE_AUDIO) {
                STOP_ACTIVE_AUDIO();
            }
            STOP_ACTIVE_AUDIO = onStopActiveAudio;
        }
        else if (STOP_ACTIVE_AUDIO === onStopActiveAudio) {
            STOP_ACTIVE_AUDIO = () => { };
        }
    }, [playing, onStopActiveAudio]);
    useEffect(() => {
        setPosition(seek);
    }, [seek]);
    const handleSeek = (e) => {
        if (e.buttons !== 1 || !e.target) {
            setSeeking(false);
            return true;
        }
        if (e.currentTarget instanceof HTMLElement) {
            const target = e.currentTarget;
            const padding = 17;
            const width = target.clientWidth - 2 * padding;
            const xRel = e.clientX - target.getBoundingClientRect().left - padding;
            const seekTo = Math.min(duration, (Math.max(0, xRel) / width) * duration);
            setSeeking(true);
            setSeek(seekTo);
        }
        return true;
    };
    return (React.createElement("div", { className: `audioplayer-seekbar ${theme}` },
        React.createElement(AudioWrapper, Object.assign({}, {
            playback: playing && !seeking ? "play" : "pause",
            seek,
            src,
            format,
            preload,
            onUnloaded,
            onLoading,
            onLoaded,
            onEnd,
            onAudioChange,
            onPlaying,
            onLoadError,
            onPlayError
        })),
        React.createElement("div", { className: "audioplayer-item" },
            React.createElement("div", { className: "circle-wrap" },
                React.createElement("div", { className: "circle-1" }),
                React.createElement("div", { className: "circle-2" }),
                React.createElement("div", { className: "circle-3" })),
            React.createElement("div", { className: "audioplayer-wrap" },
                React.createElement("div", { className: `audioplayer_button noselect ${playing && !seeking ? "playing" : ""}` },
                    React.createElement("button", { type: "button", onClick: () => setPlaying(!playing) },
                        React.createElement("img", { className: "play-img", src: ImgPlayWhite, alt: "play" }),
                        React.createElement("img", { className: "stop-img", src: ImgStopWhite, alt: "pause" }))),
                React.createElement("div", { className: "audioplayer_progress", role: "progressbar", tabIndex: -1, onMouseMove: handleSeek, onMouseDown: handleSeek, onMouseUp: () => setSeeking(false), onMouseLeave: () => setSeeking(false) },
                    React.createElement("div", { className: "audioplayer_line" },
                        React.createElement("div", { className: "audioplayer_seek", style: { width: `${(position / duration) * 100}%` } },
                            React.createElement("div", { className: "audioplayer_dot", style: { display: duration ? "block" : "none" } })))),
                React.createElement("div", { className: "audioplayer_time noselect" },
                    React.createElement("span", null, !loaded ? `–:––` : `-${secondsToTimeStr(duration - position)}`))))));
};
const AudioPlayer = (_a) => {
    var { seekbar = false } = _a, rest = __rest(_a, ["seekbar"]);
    return seekbar ? React.createElement(AudioPlayerSeekbar, Object.assign({}, rest)) : React.createElement(AudioPlayerButton, Object.assign({}, rest));
};
export default AudioPlayer;
